import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { decideProposal, proposeFromWorkflow, setAutonomy } from '@/api/proposals'
import { detectAllDomains } from '@/api/workflows'
import {
  fetchAuthMe,
  getStoredToken,
  logout as clearAuth,
  type AuthUser
} from '@/api/auth'
import { LoginPanel } from '@/components/auth/LoginPanel'
import { HeroCommand } from '@/components/command/HeroCommand'
import { ProposalInbox } from '@/components/inbox/ProposalInbox'
import { AutomationsWorkbench } from '@/components/automations/AutomationsWorkbench'
import { MobileDock } from '@/components/layout/MobileDock'
import { WorkspaceTabs } from '@/components/layout/WorkspaceTabs'
import { TabPanel } from '@/components/layout/TabPanel'
import { Topbar } from '@/components/layout/Topbar'
import { HelpSheet } from '@/components/ui/HelpSheet'
import { ToastHost } from '@/components/ui/ToastHost'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { ConnectivityBanner } from '@/components/ui/ConnectivityBanner'
import { useDashboard } from '@/hooks/useDashboard'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { useToasts } from '@/hooks/useToasts'
import { useStableToast } from '@/hooks/useStableToast'
import {
  WORKSPACE_TABS,
  useWorkspaceTabs,
  type WorkspaceTab
} from '@/hooks/useWorkspaceTabs'
import type { AutonomyLevel } from '@/types/dashboard'

const EnterprisePanel = lazy(() =>
  import('@/components/enterprise/EnterprisePanel').then((m) => ({ default: m.EnterprisePanel }))
)
const HealReplay = lazy(() =>
  import('@/components/trust/HealReplay').then((m) => ({ default: m.HealReplay }))
)
const TrustOps = lazy(() =>
  import('@/components/trust/TrustOps').then((m) => ({ default: m.TrustOps }))
)

const REQUIRE_AUTH = import.meta.env.VITE_REQUIRE_AUTH === 'true'

function PanelFallback({ label }: { label: string }) {
  return (
    <div className="empty">
      <strong>Loading {label}…</strong>
    </div>
  )
}

export default function App() {
  const { toasts, push, dismiss } = useToasts()
  const notify = useStableToast(push)
  const { tab, setTab, visited, tabs: tabMeta } = useWorkspaceTabs()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [authReady, setAuthReady] = useState(!REQUIRE_AUTH && !getStoredToken())
  const [showLogin, setShowLogin] = useState(false)

  useEffect(() => {
    const token = getStoredToken()
    if (!token) {
      setAuthReady(true)
      if (REQUIRE_AUTH) setShowLogin(true)
      return
    }
    void fetchAuthMe()
      .then((me) => {
        if (me.user) setUser(me.user)
        else if (me.auth?.via === 'local' || me.auth?.via === 'api_key') {
          setUser({
            id: me.auth.actor,
            email: me.auth.actor,
            name: me.auth.actor,
            role: me.auth.role,
            workspaceId: me.auth.workspaceId
          })
        }
        setShowLogin(false)
      })
      .catch(() => {
        clearAuth()
        if (REQUIRE_AUTH) setShowLogin(true)
      })
      .finally(() => setAuthReady(true))
  }, [])

  const { data, loading, stale, refreshing, lastError, refresh } = useDashboard()

  const [helpOpen, setHelpOpen] = useState(false)
  const [propFilter, setPropFilter] = useState<'draft' | 'all'>('draft')
  const [domainFilter, setDomainFilter] = useState('all')
  const [focusIdx, setFocusIdx] = useState(0)
  const [detecting, setDetecting] = useState(false)
  const [proposingId, setProposingId] = useState<string | null>(null)

  const proposals = data?.proposals ?? []
  const draftCount = useMemo(
    () => proposals.filter((p) => p.status === 'draft').length,
    [proposals]
  )
  const visibleProposals = useMemo(
    () => (propFilter === 'draft' ? proposals.filter((p) => p.status === 'draft') : proposals),
    [proposals, propFilter]
  )

  const onDetect = useCallback(async () => {
    setDetecting(true)
    notify('Detecting across domains…')
    try {
      const res = await detectAllDomains(data?.domains || [])
      notify(`Detected ${res.patterns} pattern(s) across ${res.domains} domain(s)`, 'ok')
      await refresh(false)
      setTab('automations')
    } catch (e) {
      notify(String((e as Error).message || e), 'err')
    } finally {
      setDetecting(false)
    }
  }, [data?.domains, notify, refresh, setTab])

  const onPropose = useCallback(
    async (id: string) => {
      setProposingId(id)
      notify('Orchestrating…')
      try {
        const res = await proposeFromWorkflow(id)
        if (res.success) {
          notify(
            `Draft ready · ${res.orchestration?.riskLevel} · ${res.orchestration?.consensus?.agreementPct ?? '—'}%`,
            'ok'
          )
          setPropFilter('draft')
          await refresh(false)
          setTab('inbox')
        } else {
          notify(`Rejected: ${res.rejection?.reason || res.error?.message || 'unknown'}`, 'err')
        }
      } catch (e) {
        notify(String((e as Error).message || e), 'err')
      } finally {
        setProposingId(null)
      }
    },
    [notify, refresh, setTab]
  )

  const onDecide = useCallback(
    async (id: string, decision: 'confirm' | 'dismiss') => {
      try {
        const res = await decideProposal(id, decision)
        if (res.success) {
          notify(`${decision} → ${res.proposal.status}`, 'ok')
          await refresh(false)
        } else {
          notify(res.error || 'Decision failed', 'err')
        }
      } catch (e) {
        notify(String((e as Error).message || e), 'err')
      }
    },
    [notify, refresh]
  )

  const onAutonomy = useCallback(
    async (level: AutonomyLevel) => {
      try {
        await setAutonomy(level)
        notify(`Autonomy → ${level}`, 'ok')
        await refresh(false)
      } catch (e) {
        notify(String((e as Error).message || e), 'err')
      }
    },
    [notify, refresh]
  )

  const focusedId = visibleProposals[focusIdx]?.id

  const shortcutHandlers = useMemo(
    () => ({
      onRefresh: () => {
        void refresh(true).then((d) => {
          if (d) notify('Synced', 'ok')
        })
      },
      onDetect,
      onHelp: () => setHelpOpen(true),
      onEscape: () => setHelpOpen(false),
      onNext: () => {
        if (tab !== 'inbox') {
          setTab('inbox')
          return
        }
        setFocusIdx((i) => Math.min(i + 1, Math.max(visibleProposals.length - 1, 0)))
      },
      onPrev: () => setFocusIdx((i) => Math.max(i - 1, 0)),
      onConfirm: () => {
        if (focusedId) void onDecide(focusedId, 'confirm')
      },
      onDismiss: () => {
        if (focusedId) void onDecide(focusedId, 'dismiss')
      },
      onTabIndex: (n: number) => {
        const id = WORKSPACE_TABS[n]
        if (id) setTab(id)
      }
    }),
    [focusedId, onDecide, onDetect, notify, refresh, setTab, tab, visibleProposals.length]
  )
  useKeyboardShortcuts(shortcutHandlers)

  const autonomy: AutonomyLevel = data?.autonomy?.level || 'suggest'
  const healthOk = data?.health.database === 'postgresql' && !stale
  const out = data?.health.outbox

  const navTabs = useMemo(
    () =>
      tabMeta.map((t) => ({
        ...t,
        badge:
          t.id === 'inbox'
            ? draftCount || undefined
            : t.id === 'automations'
              ? data?.metrics.totalWorkflows || undefined
              : undefined,
        tone: (t.id === 'inbox' && draftCount > 0
          ? 'accent'
          : t.id === 'ops' && (out?.dead ?? 0) > 0
            ? 'warn'
            : 'default') as 'default' | 'accent' | 'warn'
      })),
    [tabMeta, draftCount, data?.metrics.totalWorkflows, out?.dead]
  )

  if (!authReady) {
    return <div className="shell mono" style={{ padding: 40 }}>Loading…</div>
  }

  if (showLogin || (REQUIRE_AUTH && !user && !getStoredToken())) {
    return (
      <LoginPanel
        onLoggedIn={(u) => {
          setUser(u)
          setShowLogin(false)
          void refresh(true)
          notify(`Signed in as ${u.email}`, 'ok')
        }}
      />
    )
  }

  return (
    <ErrorBoundary boundaryName="AppShell">
      <div className="app-frame">
        <Topbar
          healthLabel={healthOk ? 'live' : stale ? 'degraded' : 'connecting'}
          healthOk={healthOk}
          aiLabel={`ai: ${data?.health.ai.keyConfigured ? 'key' : 'rules'}`}
          outboxLabel={`outbox: ${out?.pending ?? 0}p/${out?.dead ?? 0}d`}
          autonomy={autonomy}
          onAutonomy={onAutonomy}
          onHelp={() => setHelpOpen(true)}
          userLabel={user ? `${user.email} · ${user.role}` : undefined}
          onSignIn={() => setShowLogin(true)}
          onSignOut={() => {
            clearAuth()
            setUser(null)
            if (REQUIRE_AUTH) setShowLogin(true)
            else notify('Signed out', 'ok')
          }}
        />

        <WorkspaceTabs tabs={navTabs} active={tab} onChange={setTab} />

        <main className="shell ws-shell">
          <ConnectivityBanner
            stale={stale}
            lastError={lastError}
            refreshing={refreshing}
            onRetry={() => void refresh(true)}
          />

          <TabPanel id="overview" active={tab} visited={visited}>
            <ErrorBoundary boundaryName="Overview">
              <HeroCommand
                data={data}
                loading={loading}
                detecting={detecting}
                draftCount={draftCount}
                onDetect={onDetect}
                onInbox={() => setTab('inbox')}
                onAutomations={() => setTab('automations')}
                onRefresh={() =>
                  void refresh(true).then((d) => {
                    if (d) notify('Synced', 'ok')
                  })
                }
              />
            </ErrorBoundary>
          </TabPanel>

          <TabPanel id="inbox" active={tab} visited={visited}>
            <ErrorBoundary boundaryName="Inbox">
              <ProposalInbox
                proposals={proposals}
                filter={propFilter}
                focusIdx={focusIdx}
                onFilter={(f) => {
                  setPropFilter(f)
                  setFocusIdx(0)
                }}
                onConfirm={(id) => void onDecide(id, 'confirm')}
                onDismiss={(id) => void onDecide(id, 'dismiss')}
              />
            </ErrorBoundary>
          </TabPanel>

          <TabPanel id="automations" active={tab} visited={visited}>
            <ErrorBoundary boundaryName="Automations">
              <AutomationsWorkbench
                workflows={data?.workflows || []}
                domains={data?.domains || []}
                activity={data?.activity || []}
                domainFilter={domainFilter}
                proposingId={proposingId}
                onDomainFilter={setDomainFilter}
                onPropose={(id) => void onPropose(id)}
                onDetect={onDetect}
                onCopy={(id) => {
                  void navigator.clipboard
                    .writeText(id)
                    .then(() => notify('Workflow id copied', 'ok'))
                }}
                onApplied={() => void refresh(false)}
                onToast={notify}
              />
            </ErrorBoundary>
          </TabPanel>

          <TabPanel id="heal" active={tab} visited={visited}>
            <Suspense fallback={<PanelFallback label="heal" />}>
              <ErrorBoundary boundaryName="Heal">
                <HealReplay
                  workflows={(data?.workflows || []).map((w) => ({
                    id: w.id,
                    name: w.name,
                    domain: w.domain
                  }))}
                  onToast={notify}
                />
              </ErrorBoundary>
            </Suspense>
          </TabPanel>

          <TabPanel id="enterprise" active={tab} visited={visited}>
            <Suspense fallback={<PanelFallback label="enterprise" />}>
              <ErrorBoundary boundaryName="Enterprise">
                <EnterprisePanel onToast={notify} />
              </ErrorBoundary>
            </Suspense>
          </TabPanel>

          <TabPanel id="ops" active={tab} visited={visited}>
            <Suspense fallback={<PanelFallback label="ops" />}>
              <ErrorBoundary boundaryName="Ops">
                <TrustOps
                  outbox={data?.outbox || []}
                  outboxStats={data?.health.outbox}
                  audit={data?.audit || []}
                  runs={data?.runs || []}
                />
              </ErrorBoundary>
            </Suspense>
          </TabPanel>

          <footer className="status-line">
            <span>
              Flowline {data?.version || ''} · {data?.metrics.uniqueDomains ?? 0} domains ·{' '}
              {data?.metrics.uniqueSessions ?? 0} sessions · tab {tab}
            </span>
            <span>
              synced{' '}
              {data?.generatedAt ? new Date(data.generatedAt).toLocaleTimeString() : '—'}
            </span>
          </footer>
        </main>

        <MobileDock
          active={tab}
          draftCount={draftCount}
          onTab={(t: WorkspaceTab) => setTab(t)}
          onDetect={onDetect}
        />
      </div>

      <ToastHost toasts={toasts} onDismiss={dismiss} />
      <HelpSheet open={helpOpen} onClose={() => setHelpOpen(false)} />
    </ErrorBoundary>
  )
}
